import { useEffect, useState } from "react";

import { Capacitor } from "@capacitor/core";

import type { AuthClient } from "@nav-games/auth-client";
import type { Product } from "@nav-games/iap";

import { fetchPremiumProduct, purchasePremium, restorePurchases } from "../iap";

interface PremiumGateProps {
  auth: AuthClient;
}

type State =
  | { kind: "idle" }
  | { kind: "purchasing" }
  | { kind: "restoring" }
  | { kind: "success" }
  | { kind: "error"; message: string };

/// "Unlock Premium" CTA shown in any picker that has gated tiers.
/// Native-only: a web user sees a notice pointing to the mobile
/// app. On native the button calls the IAP plugin, forwards the
/// receipt to nav-games-worker, and updates `setPremium(true)` —
/// the surrounding component's `isPremium()` read on next render
/// picks up the change.
export function PremiumGate({ auth }: PremiumGateProps) {
  const native = Capacitor.isNativePlatform();
  const [product, setProduct] = useState<Product | null>(null);
  const [state, setState] = useState<State>({ kind: "idle" });

  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    void fetchPremiumProduct().then((p) => {
      if (!cancelled) setProduct(p);
    });
    return () => {
      cancelled = true;
    };
  }, [native]);

  if (!native) {
    return (
      <div className="card">
        <h3>Premium</h3>
        <p className="small muted">
          Intermediate and Expert tiers unlock with a one-off purchase —
          available in the iOS and Android apps. Sign in to the same goastern
          account in either app to keep your progress in sync.
        </p>
      </div>
    );
  }

  const price = product?.price ?? "—";
  const buying = state.kind === "purchasing";
  const restoring = state.kind === "restoring";

  return (
    <div className="card">
      <h3>Unlock Premium</h3>
      <p className="small muted">
        One-off purchase, all premium tiers across every module. Works on every
        device signed in to the same goastern account.
      </p>
      <div
        className="row"
        style={{ flexDirection: "column", gap: 8, alignItems: "stretch", marginTop: 8 }}
      >
        <button
          disabled={buying || restoring || product === null}
          onClick={async () => {
            setState({ kind: "purchasing" });
            try {
              await purchasePremium(auth);
              setState({ kind: "success" });
            } catch (e) {
              const msg = (e as Error).message;
              if (msg === "cancelled") {
                setState({ kind: "idle" });
              } else {
                setState({ kind: "error", message: msg });
              }
            }
          }}
        >
          {buying ? "Opening store…" : `Unlock for ${price}`}
        </button>
        <button
          className="secondary"
          disabled={buying || restoring}
          onClick={async () => {
            setState({ kind: "restoring" });
            try {
              await restorePurchases(auth);
              setState({ kind: "success" });
            } catch (e) {
              setState({ kind: "error", message: (e as Error).message });
            }
          }}
        >
          {restoring ? "Restoring…" : "Restore previous purchases"}
        </button>
      </div>
      {state.kind === "error" && (
        <p className="small" style={{ color: "var(--danger)", marginTop: 8 }}>
          {state.message}
        </p>
      )}
      {state.kind === "success" && (
        <p className="small" style={{ color: "var(--ok)", marginTop: 8 }}>
          Thanks — premium is now unlocked. Tap a level above to play.
        </p>
      )}
    </div>
  );
}
